import {
  AzureDevOpsOrganizationTokens,
  userConfigApiRef,
} from '@agilelab/plugin-wb-auth';
import {
  WbCard,
  WbCardContent,
  WbSensitiveKeyValueField,
  WbSensitiveKeyValueFieldEntry,
  WbSensitiveKeyValueFieldLayout,
  customAlertApiRef,
} from '@agilelab/plugin-wb-platform';
import { handleFailedResponse } from '@agilelab/plugin-wb-platform-common';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@mainstage/core-plugin-api';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import { Fragment, useEffect, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { z } from 'zod';

type UserConfigInputForm = WbSensitiveKeyValueFieldEntry & {
  providerName: string;
  title: string;
  description?: string;
  gitIntegrationProvider?: string;
  valuePlaceholder?: string;
  version: number;
  validator?: z.ZodObject<{
    key: z.ZodString;
    value: z.ZodString;
    isSensitive: z.ZodBoolean;
  }>;
};

const defaultValidator = z.object({
  key: z.string(),
  value: z.string().min(1, 'The configuration value cannot be empty'),
  isSensitive: z.boolean(),
});

// Add here additional properties until we have this object from back end
const UserConfigs: UserConfigInputForm[] = [
  {
    key: 'gitlab-token',
    providerName: 'GitLab',
    title: 'GitLab access token',
    description: 'The personal Gitlab access token for the logged user',
    isSensitive: true,
    value: '',
    valuePlaceholder: 'Gitlab access token',
    gitIntegrationProvider: 'gitlab',
    version: 1,
  },
  {
    key: 'bitbucket-server-token',
    providerName: 'Bitbucket',
    title: 'Bitbucket Server token',
    description: 'Personal Bitbucket server access token for the logged user',
    isSensitive: true,
    value: '',
    valuePlaceholder: 'Bitbucket server token',
    gitIntegrationProvider: 'bitbucket',
    version: 1,
  },
  {
    key: 'azure-devops-token',
    providerName: 'Azure DevOps',
    title: 'Global token (legacy)',
    description:
      'Used when no personal organization token is set. Legacy global PATs may no longer be supported by Azure DevOps.',
    isSensitive: true,
    value: '',
    valuePlaceholder: 'Personal Access Token',
    gitIntegrationProvider: 'azure',
    version: 1,
  },
  {
    key: 'github-token',
    providerName: 'GitHub',
    title: 'GitHub access token',
    description: 'Personal GitHub access token for the logged user',
    isSensitive: true,
    value: '',
    valuePlaceholder: 'GitHub access token',
    gitIntegrationProvider: 'github',
    version: 1,
  },
];

export function UserConfigSettings() {
  const userConfigApi = useApi(userConfigApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(customAlertApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [userConfigForms, setUserConfigForms] = useState<UserConfigInputForm[]>(
    [],
  );

  const { loading, error: fetchError } = useAsync(async () => {
    const { token } = await identityApi.getCredentials();
    const baseUrl = await discoveryApi.getBaseUrl('wb-configuration');
    const response = await fetchApi.fetch(`${baseUrl}/git-integrations`);
    await handleFailedResponse(response);
    const integrations: Record<string, unknown> = await response.json();
    const configForms: UserConfigInputForm[] = [];
    for (const conf of UserConfigs) {
      const isEnabled = conf.gitIntegrationProvider
        ? Object.prototype.hasOwnProperty.call(
            integrations,
            conf.gitIntegrationProvider,
          )
        : true;
      if (isEnabled) {
        const persistedConfig = await userConfigApi.getUserConfig({
          token: token!,
          key: conf.key,
          redactWith: '[REDACTED]',
        });
        configForms.push({
          ...conf,
          value: persistedConfig?.value ?? conf.value,
          version: 1,
        });
      }
    }
    setUserConfigForms(configForms);
  }, []);

  useEffect(() => {
    if (fetchError)
      alertApi.post({
        error: fetchError,
        severity: 'error',
      });
  }, [alertApi, fetchError]);

  const [editMode, setEditMode] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);

  const saveConfig = async (
    updatedConfig: WbSensitiveKeyValueFieldEntry,
  ): Promise<boolean> => {
    let success = false;
    try {
      const { token } = await identityApi.getCredentials();
      await userConfigApi.storeUserConfig({
        key: updatedConfig.key,
        value: updatedConfig.value,
        isSensitive: updatedConfig.isSensitive,
        token: token!,
      });
      success = true;
    } catch (error) {
      alertApi.post({
        error,
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
    return success;
  };

  const deleteConfigValue = async (configKey: string): Promise<boolean> => {
    let success = false;
    try {
      const { token } = await identityApi.getCredentials();
      await userConfigApi.deleteUserConfig({
        token: token!,
        key: configKey,
      });
      success = true;
    } catch (error) {
      alertApi.post({
        error,
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
    return success;
  };

  return (
    <Grid container direction="row" spacing={3}>
      <Grid item xs={12}>
        <WbCard title="Git configuration">
          <WbCardContent>
            {loading ? (
              <CircularProgress />
            ) : (
              <WbSensitiveKeyValueFieldLayout>
                {userConfigForms.map((config, index) => (
                  <Fragment key={config.key}>
                    {index > 0 && <Divider />}
                    <Box
                      component="section"
                      aria-labelledby={`${config.key}-provider`}
                    >
                      <Typography
                        variant="h6"
                        component="h3"
                        id={`${config.key}-provider`}
                        gutterBottom
                      >
                        {config.providerName}
                      </Typography>
                      <WbSensitiveKeyValueFieldLayout>
                        <WbSensitiveKeyValueField
                          key={config.key + config.version}
                          active={editMode === config.key}
                          editableKey={false}
                          forceSensitiveValue={config.isSensitive}
                          configName={config.title}
                          keyHelperText={config.description}
                          valueHelperText={config.valuePlaceholder ?? ''}
                          valuePlaceholder={config.valuePlaceholder ?? ''}
                          deleteDialogTitle="Clear user configuration"
                          deleteDialogContent={
                            <span>
                              Are you sure you want to clear the value of{' '}
                              <strong>{config.title}</strong>?
                            </span>
                          }
                          deleteDialogConfirmBtnText="Clear"
                          actionsEnabled={editMode === undefined}
                          isSaving={isSaving}
                          initialValue={{
                            key: config.key,
                            value: config.value,
                            isSensitive: config.isSensitive,
                          }}
                          validator={config.validator ?? defaultValidator}
                          onStartEditMode={() => setEditMode(config.key)}
                          onCancelEditMode={() => setEditMode(undefined)}
                          onSaveAction={async updatedValue => {
                            const success = await saveConfig(updatedValue);
                            if (success) {
                              setUserConfigForms(oldForms => {
                                const newForms = [...oldForms];
                                newForms[index] = {
                                  ...oldForms[index],
                                  ...updatedValue,
                                  version: oldForms[index].version + 1,
                                };
                                return newForms;
                              });
                              setEditMode(undefined);
                            }
                          }}
                          onDeleteAction={async () => {
                            const success = await deleteConfigValue(config.key);
                            if (success) {
                              setUserConfigForms(oldForms =>
                                oldForms.map(form =>
                                  form.key === config.key
                                    ? {
                                        ...form,
                                        value: '',
                                        version: form.version + 1,
                                      }
                                    : form,
                                ),
                              );
                            }
                          }}
                        />
                        {config.gitIntegrationProvider === 'azure' && (
                          <AzureDevOpsOrganizationTokens
                            disabled={editMode !== undefined}
                            onEditModeChange={setEditMode}
                          />
                        )}
                      </WbSensitiveKeyValueFieldLayout>
                    </Box>
                  </Fragment>
                ))}
              </WbSensitiveKeyValueFieldLayout>
            )}
          </WbCardContent>
        </WbCard>
      </Grid>
    </Grid>
  );
}
